import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { UX2 } from '@wsb/guac-widget-core';
import dataAids from '../../../../constants/dataAids';
import ProductDetailAssets, { ONE_COLUMN } from '../../products/ProductDetailAssets';
import CheckoutButtonsWrapper from '../../products/details/CheckoutButtonsWrapper';
import { ProductReviews } from '../../products/Yotpo';
import CommonDetails from '../CommonDetails';
import getStyles from '../getStyles';
import SimilarProducts from '../SimilarProducts';
import ProductLabels from '../../products/details/ProductLabels';
import { mapContextToProps } from '../../OlsShopContext';
import { getContentWidth, getContentHeight, getEntireHeight } from '../../utils/DOMElement';
import useDevice, { devices } from '../../../hooks/useDevice';
import SocialSharing from '../../products/details/SocialSharing';
import { isDescriptionHtml } from '../../utils/ProductUtils';

const labelsContainer = {
  '@xs': {
    display: 'block'
  },
  '@sm': {
    display: 'none'
  }
};

const resetStyles = element => {
  element.style.width = null;
  element.style.top = null;
  element.style.position = null;
};

function ProductDetail3(props) {
  const [device] = useDevice();
  const {
    Grid,
    Grid: { Cell }
  } = UX2.Component;
  const { Block, Text } = UX2.Element;
  const styles = getStyles(props);
  const { showSimilarProducts, similarProducts = [] } = props;
  const isMobile = device === devices.MOBILE || props.viewDevice === 'MOBILE_RENDER_DEVICE';
  const olsStickyContainer = document?.querySelector('[data-block-id="ols-sticky-container"]');
  const pdText = document?.querySelector('[data-block-id="product-details-text-container"]');

  const stick = () => {
    if (props.renderMode !== 'PUBLISH') return;
    if (!olsStickyContainer) return;
    if (!pdText) return;

    const maxTop = getContentHeight(olsStickyContainer) - getEntireHeight(pdText);

    if (isMobile || maxTop < 10) {
      // Don't do anything when:
      // - In mobile
      // - Or olsStickyContainer and pdText are about the same height (with a buffer of 10px)
      resetStyles(pdText);
      return;
    }

    let fromTop = 0;
    const stickyNavHeight = document?.querySelector('[data-stickynav="true"]')?.offsetHeight || 0;
    const olsStickyFromTop = olsStickyContainer.getBoundingClientRect().top * -1;

    if (olsStickyFromTop > 0) {
      fromTop += olsStickyFromTop;

      if (stickyNavHeight) {
        fromTop += stickyNavHeight;
      }
    }

    if (olsStickyFromTop + stickyNavHeight > 0) {
      // When olsStickyContainer's top is passed
      if (fromTop > 0 && maxTop > 0 && fromTop >= maxTop) {
        // Don't allow pdText to overflow past the bottom of olsStickyContainer
        pdText.style.top = `${maxTop}px`;
        pdText.style.position = 'relative';
      } else if (maxTop > 10) {
        pdText.style.width = `${getContentWidth(olsStickyContainer)}px`;
        pdText.style.top = `${stickyNavHeight}px`;
        pdText.style.position = 'fixed';
      }
    } else {
      resetStyles(pdText);
    }
  };

  useEffect(() => {
    stick();
  });

  useEffect(() => {
    if (props.renderMode === 'PUBLISH' && olsStickyContainer) {
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            document.addEventListener('scroll', stick);
          } else {
            document.removeEventListener('scroll', stick);
            resetStyles(pdText);
          }
        });
      }, {});

      observer.observe(olsStickyContainer);

      return () => {
        observer.disconnect();
        document.removeEventListener('scroll', stick);
      };
    }
  }, [props.renderMode, olsStickyContainer]);

  return (
    <Block style={{ position: 'relative' }}>
      <Grid size={ 12 }>
        <Cell xs={ 12 } sm={ 7 }>
          <Block style={ styles.assetsWrapper }>
            <ProductDetailAssets
              layout={ ONE_COLUMN }
              renderMode={ props.renderMode }
              product={ props.product }
              currentAssetIndex={ props.currentAssetIndex }
              onClickThumbnail={ props.onClickThumbnail }
              assetChangedByVariantSelection={ props.assetChangedByVariantSelection }
              assetsVisible={ 5 }
              fetching={ props.fetching || props.processing || props.inSession }
              pdpImageCropMethod={ props.pdpImageCropMethod }
              pdpImageShape={ props.pdpImageShape }
              pdpLayout={ props.pdpLayout }
            />
          </Block>
        </Cell>
        <Cell xs={ 12 } sm={ 5 } data-block-id='ols-sticky-container'>
          <Block
            data-block-id='product-details-text-container'
            style={{
              marginTop: '20px',
              marginBottom: '40px'
            }}
          >
            <Block style={{ marginBottom: 'large' }}>
              <Grid size={ 4 }>
                <Cell style={{ minWidth: '100%' }} xs={ 4 } md={ 3 }>
                  <UX2.Element.Heading.Middle
                    data-aid={ dataAids.PRODUCT_NAME_RENDERED }
                    tag='h1'
                    children={ props.product.name }
                    style={ styles.name }
                    group='Group'
                    featured
                  />
                  { (props.product.ship_free || props.product.limited_edition) && (
                    <Block style={ labelsContainer }>
                      <ProductLabels product={ props.product } translate={ props.translate } />
                    </Block>
                  ) }
                  <CommonDetails { ...props } showProductLabels={ true } />
                </Cell>
              </Grid>
            </Block>
            <CheckoutButtonsWrapper
              renderMode={ props.renderMode }
              category={ props.category }
              prices={ props.determinePrice() }
              { ...props.baseState }
              requiredFieldsBlank={ props.requiredFieldsBlank }
              isSelectedVariantInStock={ props.isSelectedVariantInStock }
              handleAddToCart={ props.handleAddToCart }
              handleApplePayClick={ props.handleApplePayClick }
              getOrderData={ props.getVariantData }
              phoneRequired={ props.stripeConfig.require_phone_number }
              isPaymentRequestEnabled={ props.stripeConfig.with_payment_request }
              isPoyntPaymentRequestEnabled={ props.poyntConfig.with_apple_pay }
              translate={ props.translate }
              showFacebook={ props.showFacebook }
              showTwitter={ props.showTwitter }
              domainName={ props.domainName }
              setHasAttemptedPurchase={ props.setHasAttemptedPurchase }
              isStickyCheckoutFooterEnabled={ props.isStickyCheckoutFooterEnabled }
            />
            <SocialSharing
              showFacebook={ props.showFacebook }
              showTwitter={ props.showTwitter }
              domainName={ props.domainName }
              translate={ props.translate }
              renderMode={ props.renderMode }
            />
            { isDescriptionHtml(props.description) ? (
              <Text
                data-aid={ dataAids.PRODUCT_DESCRIPTION_RENDERED }
                dangerouslySetInnerHTML={{ __html: props.description }}
              />
            ) : (
              <Text
                richtext
                data-aid={ dataAids.PRODUCT_DESCRIPTION_RENDERED }
                children={ props.description }
              />
            ) }
          </Block>
        </Cell>
      </Grid>
      <Grid size={ 12 }>
        <Cell xs={ 12 }>
          <ProductReviews product={ props.product } renderMode={ props.renderMode } />
        </Cell>
      </Grid>
      { showSimilarProducts && similarProducts.length > 0 && <SimilarProducts { ...props } /> }
    </Block>
  );
}

ProductDetail3.propTypes = {
  assetChangedByVariantSelection: PropTypes.bool,
  baseState: PropTypes.object,
  category: PropTypes.string,
  currentAssetIndex: PropTypes.number,
  description: PropTypes.string,
  determinePrice: PropTypes.func,
  fetching: PropTypes.bool,
  handleAddToCart: PropTypes.func,
  handleApplePayClick: PropTypes.func,
  getVariantData: PropTypes.func,
  inSession: PropTypes.bool,
  isSelectedVariantInStock: PropTypes.bool,
  onClickThumbnail: PropTypes.func,
  pdpImageCropMethod: PropTypes.string,
  processing: PropTypes.bool,
  product: PropTypes.object,
  stripeConfig: PropTypes.object,
  poyntConfig: PropTypes.object,
  renderMode: PropTypes.string,
  requiredFieldsBlank: PropTypes.bool,
  translate: PropTypes.func,
  pdpImageShape: PropTypes.string,
  showSimilarProducts: PropTypes.bool,
  similarProducts: PropTypes.array,
  viewDevice: PropTypes.string,
  pdpLayout: PropTypes.string.isRequired,
  showFacebook: PropTypes.bool,
  showTwitter: PropTypes.bool,
  domainName: PropTypes.string,
  setHasAttemptedPurchase: PropTypes.func,
  useBruce: PropTypes.bool,
  isStickyCheckoutFooterEnabled: PropTypes.bool
};

export default mapContextToProps(ProductDetail3, context => ({
  viewDevice: context.viewDevice
}));
